import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MInputField from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { MButton } from "@/components/ui/atoms/MButtons";
import FormHeader from "@/components/ui/organisms/MHeader";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import useHealthMemory from "@/hooks/useHealthMemory";
import { RecorderState } from "@/hooks/useVoiceNoteRecorder";
import { getMemberInfo } from "@/hooks/useMembers";
import { getMemberNickFirst } from "@/utils/member";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";
import VoiceNoteRecorder from "./VoiceNoteRecorder";
import styles from "./HealthMemoryForm.module.css";

// Placeholder type and hooks for demonstration
interface HealthMemoryFormData {
  title: string;
  year: string;
  month: string;
  details: string;
}

const HealthMemoryForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  const {
    createHealthMemory,
    updateHealthMemory,
    healthMemories,
    fetchHealthMemory,
    uploadVoiceNote,
    getVoiceNoteUrl,
  } = useHealthMemory(memberId);

  const params = new URLSearchParams(location.search);
  const mode = params.get("mode");
  const id = params.get("id");
  const yearParam = params.get("year");

  // In a real implementation, fetch the memory by id for edit mode
  const [form, setForm] = useState<HealthMemoryFormData>({
    title: "",
    year: yearParam || "",
    month: "",
    details: "",
  });
  const [initialForm, setInitialForm] = useState<HealthMemoryFormData | null>(
    null
  );
  const [loading, setLoading] = useState(false);

  // Voice note recorder state (D-56-03, D-56-04, D-56-06)
  const [recorderWavBlob, setRecorderWavBlob] = useState<Blob | null>(null);
  const [recorderState, setRecorderState] = useState<RecorderState>("idle");
  const [prefillHasVoiceNote, setPrefillHasVoiceNote] = useState(false);
  const [prefillVoiceNoteDuration, setPrefillVoiceNoteDuration] = useState<
    number | undefined
  >(undefined);

  // Prefill form in edit mode (fetch from backend)
  useEffect(() => {
    if (mode === "edit" && id) {
      fetchHealthMemory(id).then((memory) => {
        if (memory) {
          let details = memory.details || "";
          if (
            memory.transcriptStatus === "completed" &&
            memory.transcript &&
            !details.includes(memory.transcript)
          ) {
            details = details.trim()
              ? `${details}\n\n${memory.transcript}`
              : memory.transcript;
          }
          const fetched = {
            title: memory.title || "",
            year: memory.year ? memory.year.toString() : "",
            month: memory.month ? memory.month.toString() : "",
            details,
          };
          setForm(fetched);
          setInitialForm(fetched);
          // D-56-06: prefill hybrid mini-player when a saved note exists
          setPrefillHasVoiceNote(!!memory.hasVoiceNote);
          setPrefillVoiceNoteDuration(memory.voiceNoteDuration);
        }
      });
    } else {
      // Track page load for add mode
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_ADD,
      });
    }
  }, [mode, id]);

  const isFormValid =
    form.title.trim().length > 0 &&
    form.year.length === 4 &&
    form.month.length > 0;
  const isDirty =
    mode !== "edit" ||
    !initialForm ||
    form.title !== initialForm.title ||
    form.year !== initialForm.year ||
    form.month !== initialForm.month ||
    form.details !== initialForm.details ||
    recorderWavBlob !== null;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Track done click
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.DOCUMENTS,
        screenName: ANALYTICS_SCREEN_NAMES.HEALTH_MEMORY_ADD_CONFIRM,
      });

      let savedId: string | null = null;
      if (mode === "edit" && id) {
        await updateHealthMemory(id, {
          title: form.title,
          year: Number(form.year),
          month: Number(form.month),
          details: form.details,
        });
        savedId = id;
      } else {
        const newMemory = await createHealthMemory({
          title: form.title,
          year: Number(form.year),
          month: Number(form.month),
          details: form.details,
          memberId,
        });
        savedId = newMemory?.id ?? null;
      }

      // D-56-01: upload WAV after memory saves (sequential, single spinner)
      if (savedId && recorderWavBlob) {
        const uploadResult = await uploadVoiceNote(savedId, recorderWavBlob);
        // D-56-02: upload failure shows toast but navigation still proceeds
        if (!uploadResult) {
          showToast({
            message: t("healthMemory.voiceNote.uploadFailToast"),
            variant: "error",
          });
        }
      }

      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`profile-page-root flex flex-col ${styles.root}`}>
      <FormHeader
        module="Health memory notes"
        memberInfo={memberInfo}
        title={
          memberInfo.onBehalf
            ? `${getMemberNickFirst(memberInfo)}'s memory note`
            : "Your memory note"
        }
        description="Note an injury, condition, or episode you recall that give caregivers the full picture."
        hideLearnMore
      />
      <MSpacer mt={24} />
      <div className="flex flex-col gap-[32px]">
        <MInputField
          label="What was the event?"
          value={form.title}
          placeholder="e.g. Asthma episode, knee injury"
          size="md"
          onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
        />
        <MDateInputField
          hideDate
          label="When did this happen?"
          minYear={memberInfo.dobYear || 1940}
          value={
            form.year && form.month
              ? new Date(Number(form.year), Number(form.month) - 1)
              : undefined
          }
          initialPickerValue={
            form.year
              ? new Date(
                  Number(form.year),
                  form.month ? Number(form.month) - 1 : new Date().getMonth()
                )
              : undefined
          }
          onDateChange={(date) =>
            setForm((f) => ({
              ...f,
              year: date.getFullYear().toString(),
              month: (date.getMonth() + 1).toString(),
            }))
          }
          formatDate={(date) =>
            date
              ? `${date.toLocaleString("default", {
                  month: "long",
                })} ${date.getFullYear()}`
              : ""
          }
          placeholder="Not Answered"
          size="md"
          className="add-chip-input"
          datePickerTitle="Select month and year"
        />
        <MTextArea
          label="Add details (optional)"
          value={form.details}
          placeholder="Add symptoms, notes, or treatment details"
          className="h-[135px]"
          maxLength={1000}
          onChange={(e) => setForm((f) => ({ ...f, details: e.target.value }))}
        />
        {/* D-56-04: VoiceNoteRecorder between details field and Done button */}
        <VoiceNoteRecorder
          onBlobReady={setRecorderWavBlob}
          onStateChange={setRecorderState}
          hasSavedNote={mode === "edit" && prefillHasVoiceNote}
          savedDuration={prefillVoiceNoteDuration}
          onFetchSavedUrl={
            id ? () => getVoiceNoteUrl(id).then((r) => r?.url ?? "") : undefined
          }
          disabled={loading}
        />
      </div>
      <div className="confirm-footer mt-8">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={
            !isFormValid || loading || !isDirty || recorderState === "recording"
          }
          loading={loading}
          onClick={handleSubmit}
        >
          Done
        </MButton>
      </div>
    </div>
  );
};

export default HealthMemoryForm;
